// Result-view metrics: buried depth (derived) and aggregate summary stats used
// by the Kennzahlen panel and the map labels.

// Reads a numeric prop value, or null when missing/blank/NaN.
function readNum(prop) {
  if (!prop || prop.value === "" || prop.value == null) return null;
  const value = Number(prop.value);
  return Number.isNaN(value) ? null : value;
}

// Buried depth at a node = ground elevation (props.elevation) minus the
// calculated invert elevation (calc.invert_elevation). A positive value means
// the pipe invert lies that many meters below the surface. Returns null when
// either elevation is missing (e.g. before an optimization has run).
export function getNodeBuriedDepth(node) {
  const ground = readNum(node?.props?.find((p) => p.key === "elevation"));
  const invert = readNum(node?.calc?.find((p) => p.key === "invert_elevation"));
  if (ground == null || invert == null) return null;
  return ground - invert;
}

// Ids of nodes whose buried depth exceeds the allowed maximum (maxDepth, in
// meters). The optimization method can produce nodes that overrun the limit;
// these get surfaced as a warning and highlighted red on the map. Nodes without
// a computable depth (missing elevation/invert) are ignored.
export function findOverDepthNodeIds(elements = [], maxDepth) {
  const limit = Number(maxDepth);
  if (!Number.isFinite(limit)) return [];
  const ids = [];
  elements.forEach((node) => {
    if (node.type !== "node") return;
    const depth = getNodeBuriedDepth(node);
    if (depth != null && depth > limit) ids.push(node.id);
  });
  return ids;
}

function stats(values) {
  if (!values.length) return null;
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  values.forEach((v) => {
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  });
  return { min, max, avg: sum / values.length, count: values.length };
}

// Groups equal values into { value, count } buckets sorted ascending — used for
// the diameter distribution mini-histogram.
function countBy(values) {
  const map = new Map();
  values.forEach((v) => map.set(v, (map.get(v) || 0) + 1));
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([value, count]) => ({ value, count }));
}

// Aggregate stats for the currently displayed result elements. All lengths in
// meters, flows in l/s, diameters and depths in meters.
export function getResultSummary(elements = []) {
  const edges = elements.filter((e) => e.type === "edge");
  const nodes = elements.filter((e) => e.type === "node");

  let totalLength = 0;
  const diameters = [];
  const flowsFirst = [];
  const flowsFinal = [];

  edges.forEach((edge) => {
    const len = readNum(edge.props?.find((p) => p.key === "length"));
    if (len != null) totalLength += len;
    const d = readNum(edge.calc?.find((p) => p.key === "diameter"));
    if (d != null) diameters.push(d);
    const ff = readNum(edge.calc?.find((p) => p.key === "first_year_pipe_flow"));
    if (ff != null) flowsFirst.push(ff);
    const fl = readNum(edge.calc?.find((p) => p.key === "final_year_pipe_flow"));
    if (fl != null) flowsFinal.push(fl);
  });

  const depths = [];
  nodes.forEach((node) => {
    const bd = getNodeBuriedDepth(node);
    if (bd != null) depths.push(bd);
  });

  return {
    edgeCount: edges.length,
    nodeCount: nodes.length,
    totalLength,
    diameter: stats(diameters),
    diameterCounts: countBy(diameters),
    flowFirst: stats(flowsFirst),
    flowFinal: stats(flowsFinal),
    buriedDepth: stats(depths),
    // does the result actually carry computed values yet?
    hasResult: diameters.length > 0 || flowsFinal.length > 0,
  };
}

// Buried depth of an edge, taken as the deeper of its two end nodes (the
// governing excavation depth). Returns null when neither end has a value.
export function getEdgeBuriedDepth(edge, mapElements) {
  const start = mapElements.find((n) => n.id === edge.startNodeId);
  const end = mapElements.find((n) => n.id === edge.endNodeId);
  const values = [getNodeBuriedDepth(start), getNodeBuriedDepth(end)].filter(
    (v) => v != null
  );
  if (!values.length) return null;
  return Math.max(...values);
}
